import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Rating from '@mui/material/Rating';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TablePagination from '@mui/material/TablePagination';
import { useGuideReviews } from '@/features/guide/hooks/useGuideReviews';
import { RatingBreakdown } from '@/features/guide/components/RatingBreakdown';
import { GuideReviewListItem } from '@/features/guide/components/GuideReviewListItem';
import { GuidePageHeader } from '@/features/guide/components/GuidePageHeader';

export function GuideReviewsPage() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, isLoading, error } = useGuideReviews({
    page: page + 1,
    limit: rowsPerPage,
  });

  const summary = data?.summary;
  const reviews = data?.data ?? [];
  const total = data?.pagination.total ?? 0;

  return (
    <Box>
      <GuidePageHeader
        title="Reviews"
        subtitle="See what travelers are saying about your guided experiences."
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load reviews. Please try again.
        </Alert>
      )}

      {summary && !isLoading && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
            gap: 3,
            alignItems: 'center',
          }}
        >
          <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
            <Typography variant="h3" fontWeight={800} color="secondary.main">
              {summary.averageRating.toFixed(1)}
            </Typography>
            <Rating value={summary.averageRating} readOnly precision={0.1} sx={{ mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {summary.reviewCount} review{summary.reviewCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <RatingBreakdown summary={summary} />
        </Paper>
      )}

      {!isLoading && !error && reviews.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            No reviews yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reviews appear here after travelers complete approved connections and leave feedback.
          </Typography>
        </Paper>
      )}

      {reviews.map((review) => (
        <GuideReviewListItem key={review.id} review={review} />
      ))}

      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 20]}
        />
      )}
    </Box>
  );
}
